// Components
import { Icon, useTheme, Link } from "@fluentui/react";
import { useCallback } from "react";
// Constants
import { ContextScenarioConstants } from "@ms/uno-constants/lib/local/ExternalSourceConstants";
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { ExternalTaskSourceDisplayType } from "@ms/uno-models/lib/local/client/legacyPlanner/task/creationSource/ExternalTaskSourceDisplayType";
import { UnknownUser } from "@ms/uno-models/lib/local/client/user/UnknownUser";
import { DriveItem } from "@ms/uno-models/lib/local/client/driveItem/DriveItem";
// Icons
import loopSvg from "@ms/uno-resources/lib/local/images/logos/loop.svg";
import teamsSvg from "@ms/uno-resources/lib/local/images/logos/teams.svg";
import pageLinkSvg from "@ms/uno-resources/lib/local/images/logos/pagelink.svg";
import projectSvg from "@ms/uno-resources/lib/local/images/logos/project-compact.svg";
// Styles
import { getClassNames, getStyles } from "./ExternalSourceView.styles";
import { openInNewWindowIconProps } from "../../../../icons/icons.styles";
// Utilities
import * as React from "react";
import { css } from "@fluentui/utilities/lib/css";
import * as StringHelper from "@fluentui/utilities";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { getDocPlanImage } from "../../../../icons/IconUtilities";
import { getDocumentColorClassName } from "../../../../utilities/ColorUtilities";
// Logging
import { useLoggers } from "../../../../hooks/useLoggers";
/**
 * ExternalSourceView renders information stored in Task.CreationSource.ExternalSource
 */ export function ExternalSourceView(props) {
    const { task, getUserById, styles, plan } = props;
    const theme = useTheme();
    const { engagementLogger, traceLogger } = useLoggers();
    const classNames = getClassNames(getStyles(theme, traceLogger, styles));
    const externalSource = task.creationSource.external;
    /**
     * Callback for when the task deep link is clicked
     */ const onClickTaskDeepLink = useCallback(()=>{
        engagementLogger.logEngagement("ExternalSourceView", EngagementTelemetryConstants.TaskDeepLinkClicked, {
            taskCreationSourceType: task.taskCreationSourceType
        });
    }, [
        task.taskCreationSourceType,
        engagementLogger
    ]);
    if (task.creationSource.document == null && (externalSource == null || externalSource.displayAs === ExternalTaskSourceDisplayType.None)) {
        return null;
    }
    let externalSourceViewProperties = null;
    if (task.creationSource.document != null) {
        externalSourceViewProperties = getDocumentTaskExternalSourceViewProperties(plan);
    } else if (externalSource != null) {
        externalSourceViewProperties = getTaskExternalSourceViewProperties(externalSource, plan);
    }
    const userDisplayName = (getUserById(task.createdBy) ?? UnknownUser.getUnknownUser(task.createdBy)).formattedDisplayName;
    const subtitleString = externalSourceViewProperties && StringHelper.format(Strings.ExternalSourceView_TaskInfoSubtitle, userDisplayName, task.createdDate.format("L"));
    return externalSourceViewProperties && /*#__PURE__*/ React.createElement("div", {
        className: classNames.root
    }, /*#__PURE__*/ React.createElement("div", {
        className: classNames.externalSourceContentLeft
    }, !task.isProjectTask() && /*#__PURE__*/ React.createElement("div", {
        className: classNames.externalSourceHeader
    }, /*#__PURE__*/ React.createElement("img", {
        src: externalSourceViewProperties.svg,
        className: classNames.externalSourceIcon,
        alt: ""
    }), externalSourceViewProperties.titleString), /*#__PURE__*/ React.createElement("div", {
        className: classNames.externalSourceSubHeader
    }, subtitleString)), !task.isProjectTask() && externalSourceViewProperties.webUrl && /*#__PURE__*/ React.createElement(Link, {
        className: css(classNames.externalUrl, externalSourceViewProperties.linkColorClass),
        href: externalSourceViewProperties.webUrl,
        target: "_blank",
        rel: "noopener noreferrer",
        onClick: onClickTaskDeepLink
    }, externalSourceViewProperties.buttonText, /*#__PURE__*/ React.createElement(Icon, openInNewWindowIconProps)));
}
/**
 * Helper method to get external source view properties for non Document tasks
 * @param externalSource A task's externalSource
 */ const getTaskExternalSourceViewProperties = (externalSource, plan)=>{
    let titleString = externalSource.getSyncContextDisplayText();
    const buttonText = Strings.ExternalSourceView_Navigate;
    let svg;
    let linkColorClass;
    switch(externalSource.contextScenarioId){
        case ContextScenarioConstants.meetingNotes:
            svg = loopSvg;
            linkColorClass = "loopColor";
            break;
        case ContextScenarioConstants.loop:
            svg = loopSvg;
            linkColorClass = "loopColor";
            break;
        case ContextScenarioConstants.teamsMessage:
            svg = teamsSvg;
            linkColorClass = "teamsColor";
            break;
        case ContextScenarioConstants.project:
            if (!plan?.title) {
                return null;
            }
            titleString = plan.title;
            svg = projectSvg;
            linkColorClass = "projectColor";
            break;
        default:
            svg = pageLinkSvg;
            linkColorClass = "";
            break;
    }
    return {
        titleString: titleString,
        svg: svg,
        buttonText: buttonText,
        linkColorClass: linkColorClass,
        webUrl: externalSource?.webUrl
    };
};
/**
 * Helper method to get external source view properties for Document tasks
 */ const getDocumentTaskExternalSourceViewProperties = (plan)=>{
    // Currently we still need plan title to get document type
    if (!plan) {
        return null;
    }
    const titleString = plan.title;
    const docType = DriveItem.GetFileType(titleString);
    const svg = getDocPlanImage(docType);
    const buttonText = Strings.ExternalSourceView_Navigate;
    const linkColorClass = getDocumentColorClassName(docType);
    return {
        titleString: titleString,
        svg: svg,
        buttonText: buttonText,
        linkColorClass: linkColorClass,
        webUrl: "https://www.office.com"
    };
};
